import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserGroupIcon, StarIcon, ClockIcon, ExclamationTriangleIcon,
  ArrowDownTrayIcon, ChevronLeftIcon, ChevronRightIcon, ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import Avatar from '../components/Avatar';
import { adminAPI } from '../services/api';
import styles from './CRMPage.module.css';

const SEGMENTS = [
  {
    key: 'registered_no_vip',
    label: 'Registered — No VIP',
    description: 'Users who downloaded & registered but never purchased a VIP membership.',
    icon: UserGroupIcon,
    color: '#6366F1',
    bg: 'rgba(99,102,241,0.1)',
    campaign: 'Convert to VIP',
  },
  {
    key: 'vip_no_sales',
    label: 'VIP — No Purchases Yet',
    description: 'Active VIP members who have not made any recorded purchases.',
    icon: StarIcon,
    color: '#F59E0B',
    bg: 'rgba(245,158,11,0.1)',
    campaign: 'Drive First Purchase',
  },
  {
    key: 'expiring_30',
    label: 'Expiring in 30 Days',
    description: 'VIP memberships expiring within the next 30 days — urgent renewal window.',
    icon: ExclamationTriangleIcon,
    color: '#EF4444',
    bg: 'rgba(239,68,68,0.1)',
    campaign: 'Urgent Renewal',
  },
  {
    key: 'expiring_60',
    label: 'Expiring in 60 Days',
    description: 'VIP memberships expiring within 31–60 days — proactive renewal campaign.',
    icon: ClockIcon,
    color: '#F97316',
    bg: 'rgba(249,115,22,0.1)',
    campaign: 'Renewal Reminder',
  },
  {
    key: 'churned',
    label: 'Churned — Expired VIP',
    description: 'Members whose VIP has expired and not been renewed — win-back opportunity.',
    icon: ArrowPathIcon,
    color: '#64748B',
    bg: 'rgba(100,116,139,0.1)',
    campaign: 'Win-Back Campaign',
  },
];

function DaysLeftBadge({ days }) {
  if (days === null || days === undefined || days === 0) return null;
  const color = days <= 7 ? '#EF4444' : days <= 30 ? '#F59E0B' : '#22C55E';
  return <span style={{ color, fontWeight: 600, fontSize: 13 }}>{days}d</span>;
}

export default function CRMPage() {
  const navigate = useNavigate();
  const [counts, setCounts]         = useState(null);
  const [active, setActive]         = useState(null);
  const [members, setMembers]       = useState([]);
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(false);
  const [countLoading, setCountLoading] = useState(true);
  const [exporting, setExporting]   = useState(false);

  useEffect(() => {
    adminAPI.getCRMSegments().then(r => { setCounts(r.segments); setCountLoading(false); }).catch(() => setCountLoading(false));
  }, []);

  const loadSegment = useCallback(async (key, p = 1) => {
    setLoading(true);
    try {
      const r = await adminAPI.getCRMSegment(key, p);
      setMembers(r.members);
      setTotal(r.total);
      setTotalPages(r.totalPages);
      setPage(p);
    } finally { setLoading(false); }
  }, []);

  const selectSegment = (seg) => {
    setActive(seg);
    setPage(1);
    loadSegment(seg.key, 1);
  };

  const exportCSV = async () => {
    if (!active) return;
    setExporting(true);
    try {
      const r = await adminAPI.exportCRMSegment(active.key);
      const url = URL.createObjectURL(new Blob([r], { type: 'text/csv' }));
      const a = document.createElement('a'); a.href = url; a.download = `crm-${active.key}.csv`; a.click();
      URL.revokeObjectURL(url);
    } finally { setExporting(false); }
  };

  const activeMeta = SEGMENTS.find(s => s.key === active?.key);

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>CRM Segments</h1>
          <p className={styles.pageSub}>Target members by lifecycle stage to run focused campaigns.</p>
        </div>
      </div>

      {/* Segment cards */}
      <div className={styles.segmentGrid}>
        {SEGMENTS.map(seg => {
          const Icon = seg.icon;
          const count = counts ? counts[seg.key === 'registered_no_vip' ? 'registeredNoVip' : seg.key === 'vip_no_sales' ? 'vipNoSales' : seg.key === 'expiring_30' ? 'expiring30' : seg.key === 'expiring_60' ? 'expiring60' : 'churned'] : null;
          const isActive = active?.key === seg.key;
          return (
            <button
              key={seg.key}
              className={`${styles.segCard} ${isActive ? styles.segCardActive : ''}`}
              style={isActive ? { borderColor: seg.color, boxShadow: `0 0 0 3px ${seg.color}22` } : {}}
              onClick={() => selectSegment(seg)}
            >
              <div className={styles.segCardTop}>
                <div className={styles.segIcon} style={{ background: seg.bg, color: seg.color }}>
                  <Icon width={20} height={20} />
                </div>
                {isActive && <CheckCircleIcon width={18} height={18} style={{ color: seg.color }} />}
              </div>
              <div className={styles.segCount} style={{ color: seg.color }}>
                {countLoading ? '—' : count ?? '—'}
              </div>
              <div className={styles.segLabel}>{seg.label}</div>
              <div className={styles.segDesc}>{seg.description}</div>
              <div className={styles.segCampaignTag} style={{ background: seg.bg, color: seg.color }}>
                {seg.campaign}
              </div>
            </button>
          );
        })}
      </div>

      {/* Member list */}
      {active && (
        <div className={styles.listCard}>
          <div className={styles.listHeader}>
            <div>
              <h2 className={styles.listTitle}>{activeMeta?.label}</h2>
              <p className={styles.listSub}>{total} member{total !== 1 ? 's' : ''} in this segment</p>
            </div>
            <button className="btn-primary" onClick={exportCSV} disabled={exporting} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <ArrowDownTrayIcon width={15} height={15} />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>

          <div className={styles.tableWrap}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Mobile</th>
                  <th>Member Code</th>
                  <th>Registered</th>
                  <th>Expiry</th>
                  <th>Days Left</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 7 }).map((__, j) => (
                        <td key={j}><div className="skeleton" style={{ height: 16, width: j === 0 ? 140 : 80 }} /></td>
                      ))}
                    </tr>
                  ))
                ) : members.length === 0 ? (
                  <tr><td colSpan={7}>
                    <div className="empty-state">
                      <p>No members in this segment.</p>
                    </div>
                  </td></tr>
                ) : members.map(m => (
                  <tr key={m.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar name={`${m.first_name} ${m.last_name}`} size={32} />
                        <span style={{ fontWeight: 600 }}>{m.first_name} {m.last_name}</span>
                      </div>
                    </td>
                    <td>{m.mobile}</td>
                    <td>{m.member_code ? <code style={{ fontSize: 12, background: 'var(--bg-subtle)', padding: '2px 6px', borderRadius: 4 }}>{m.member_code}</code> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td>{m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}</td>
                    <td>{m.expiry_date ? new Date(m.expiry_date).toLocaleDateString() : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td><DaysLeftBadge days={m.days_remaining} /></td>
                    <td>
                      <button className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => navigate(`/members/${m.id}`)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button className="btn-ghost" onClick={() => loadSegment(active.key, page - 1)} disabled={page === 1}>
                <ChevronLeftIcon width={16} height={16} />
              </button>
              <span className={styles.pageInfo}>Page {page} of {totalPages}</span>
              <button className="btn-ghost" onClick={() => loadSegment(active.key, page + 1)} disabled={page === totalPages}>
                <ChevronRightIcon width={16} height={16} />
              </button>
            </div>
          )}
        </div>
      )}

      {!active && (
        <div className="empty-state" style={{ marginTop: 32 }}>
          <UserGroupIcon width={40} height={40} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
          <p>Select a segment above to view members and export for campaigns.</p>
        </div>
      )}
    </div>
  );
}
